import { register } from "@rill/loader";
import moment from "moment";
import { DATE_FORMATS } from "../constants";
import { isAfterToday } from "../utils/date-helpers";
import { getSessionValue } from "../utils/session-helper";

register(
  {
    name: "cycleExercises",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api }, { cycle, program, week, allWeeks }) => {
    const res = await api("cycle-exercises", {
      query: {
        cycle,
        program,
        week,
        allWeeks,
      },
    });

    return await res.json();
  }
);

register(
  {
    name: "programExercises",
    meta: ["no-bust", "cycle"],
    ttl: "2 hours",
  },
  async ({ api }, { programId }) => {
    const res = await api("program-exercises", {
      query: {
        programId,
      },
    });

    return await res.json();
  }
);

register(
  {
    name: "activeExercises",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async (context, query) => {
    const { api } = context;
    const {
      start,
      end,
      proxyAthlete,
      reqSource,
      organizationCoach = undefined,
    } = query;

    let queryExercise = {
      limit: false,
      sort: { order: 1, date: 1 },
      fields: [
        "_id",
        "primary",
        "secondary",
        "secondaryRepMaximum",
        "primaryMuscleGroup",
        "secondaryMuscleGroup",
        "sets",
        "repMaximums",
        "notes",
        "athleteNotes",
        "date",
        "utcDate",
        "order",
        "type",
        "cycle",
        "parameters",
        "instructions",
        "nextSuperset",
        "complex",
        "complexName",
        "indexInComplex",
        "complexInstance",
        "subType",
        "duration",
        "totalRounds",
        "structure",
        "restBetweenRounds",
        "resultMeasurement",
        "ladderDirection",
        "ladderIncrement",
        "metconResult",
        "customName",
        "repsPerSide",
        "tempo",
      ],
      utcDate: {
        $gte: Number.parseInt(moment(start).format("YYYYMMDD"), 10),
        $lte: Number.parseInt(moment(end).format("YYYYMMDD"), 10),
      },
      $or: [{ cycle: { $exists: false } }, { "cycle.isScheduled": true }],
      proxyAthlete,
      reqSource,
      withThreads: true,
    };

    if (organizationCoach) {
      queryExercise = { ...queryExercise, organizationCoach };
    }

    const res = await api("exercise", {
      query: queryExercise,
    });

    return await res.json().then((exercises) => {
      return exercises.map((exercise) => {
        exercise.originalDate = exercise.date.slice(0); //CH-980

        // Removed calendar timezone updating, task #700
        return exercise;
      });
    });
  }
);

register(
  {
    name: "activeSections",
    meta: ["no-bust", "exercise", "exercise-section"],
    ttl: "2 hours",
  },
  async (context, query) => {
    const { api } = context;
    const {
      start,
      end,
      proxyAthlete,
      reqSource,
      organizationCoach = undefined,
    } = query;

    let sectionQuery = {
      limit: false,
      sort: { order: 1 },
      utcDate: {
        $gte: Number.parseInt(moment(start).format("YYYYMMDD"), 10),
        $lte: Number.parseInt(moment(end).format("YYYYMMDD"), 10),
      },
      proxyAthlete,
      reqSource,
    };

    if (organizationCoach) {
      sectionQuery = { ...sectionQuery, organizationCoach };
    }

    const res = await api("exercise-section", { query: sectionQuery });
    return await res.json();
  }
);

register(
  {
    name: "editSections",
    meta: ["no-bust", "exercise", "exercise-section"],
    ttl: "2 hours",
  },
  async (
    { api },
    { cycle, program, proxyAthlete, organizationCoach = undefined }
  ) => {
    const query = {
      limit: false,
      "cycle.id": cycle,
      proxyAthlete,
      sort: { order: 1 },
      organizationCoach,
    };

    if (program) {
      query["cycle.program"] = program;
    }

    const res = await api("exercise-section", { query });
    return await res.json();
  }
);

register(
  {
    name: "currentYearExercises",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async (context, query) => {
    const { api } = context;
    const { start, end, proxyAthlete, reqSource } = query;
    const res = await api("exercise", {
      query: {
        limit: false,
        sort: { date: -1, order: 1 },
        fields: ["primaryMuscleGroup", "sets", "date", "utcDate"],
        utcDate: {
          $gte: Number.parseInt(
            moment(start).subtract(2, "months").format("YYYYMMDD"),
            10
          ),
          $lte: Number.parseInt(
            moment(end).add(1, "months").format("YYYYMMDD"),
            10
          ),
        },
        $or: [{ cycle: { $exists: false } }, { "cycle.isScheduled": true }],
        proxyAthlete,
        reqSource,
      },
    });

    return await res.json().then((exercises) => {
      return exercises.map((exercise) => {
        exercise.originalDate = exercise.date.slice(0); //CH-980

        // Removed calendar timezone updating, task #700
        return exercise;
      });
    });
  }
);

/**Current date expected to be in YYYY-MM-DD */
register(
  {
    name: "exercises",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async ({ api }, { currentDate, proxyAthlete }) => {
    const date = moment(currentDate, DATE_FORMATS.DATE_DAY_FORMAT);
    const startDate = Number.parseInt(
      date
        .clone()
        .subtract(3, "month")
        .startOf("month")
        .format(DATE_FORMATS.UTC),
      10
    );
    const endDate = Number.parseInt(
      date.clone().endOf("month").format(DATE_FORMATS.UTC),
      10
    );
    const query = {
      limit: false,
      sort: { utcDate: -1 },
      utcDate: {
        $gte: startDate,
        $lte: endDate,
      },
      $or: [{ cycle: { $exists: false } }, { "cycle.isScheduled": true }],
    };

    if (proxyAthlete) {
      query.proxyAthlete = proxyAthlete;
    }

    const res = await api("exercise", { query });
    const resJson = await res.json();
    return resJson;
  }
);

register(
  {
    name: "copyExercises",
    meta: ["exercise"],
  },
  async ({ api }, { date, proxyAthlete }) => {
    const res = await api("exercise", {
      query: {
        limit: false,
        sort: { order: 1 },
        fields: [
          "_id",
          "primary",
          "secondary",
          "sets",
          "repMaximums",
          "notes",
          "date",
          "utcDate",
          "order",
        ],
        utcDate: {
          $gte: Number.parseInt(
            moment(date).startOf("month").format("YYYYMMDD"),
            10
          ),
          $lte: Number.parseInt(
            moment(date).endOf("month").format("YYYYMMDD"),
            10
          ),
        },
        proxyAthlete,
      },
    });

    return await res.json();
  }
);

register(
  {
    name: "editExercise",
    meta: ["exercise"],
  },
  async (
    { locals, api, assert },
    { exercise, proxyAthlete, organizationCoach = undefined }
  ) => {
    if (!exercise) return { _id: "", sets: [], primary: "squat" };
    let query = { _id: exercise, proxyAthlete };

    const organizationCoachQuery =
      locals.query.organizationCoach || organizationCoach;
    if (organizationCoach) {
      query = { ...query, organizationCoach: organizationCoachQuery };
    }

    const res = await api("exercise", {
      query,
    });
    const doc = (await res.json())[0];
    assert(doc, 400, "Unable to find exercise, it may have been deleted.");
    doc.sets = doc.sets || [];
    return doc;
  }
);

register(
  {
    name: "editExercises",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async (
    { api, session },
    {
      cycle,
      isScheduled,
      proxyAthlete,
      program,
      forProgramEditor = false,
      organizationCoach = undefined,
      fields = undefined,
    }
  ) => {
    const editExercises = getSessionValue(session, "editExercises");
    if (editExercises?.length) return editExercises;
    // No cycle id means "every exercise". On program overview the id lives in
    // the route, and an undefined query.cycle was cached as the whole athlete
    // (enough to blow the session document). Never fetch that.
    if (!cycle) return [];
    const query = {
      limit: false,
      "cycle.id": cycle,
      "cycle.isScheduled": isScheduled,
      proxyAthlete,
      sort: { order: 1 },
      forProgramEditor,
      organizationCoach,
    };

    if (program) {
      query["cycle.program"] = program;
    }
    if (fields) query.fields = fields;

    const res = await api("exercise", {
      query,
    });
    return await res.json();
  }
);

register(
  {
    name: "exerciseHistory",
    meta: ["no-bust", "exercise-history", "exercise"],
    ttl: "2 hours",
  },
  async (
    { api },
    { type, primary, from, to, secondary, proxyAthlete, complex }
  ) => {
    const res = await api("exercise-of-type", {
      method: "POST",
      body: {
        from: moment.utc(from, "YYYY-MM-DD").format(),
        primary,
        secondary,
        to: moment.utc(to, "YYYY-MM-DD").format(),
        type,
        proxyAthlete,
        complex,
        withThreads: true,
        //fields to return:
        projection: {
          parameters: 1,
          _id: 1,
          type: 1,
          primary: 1,
          repMaximums: 1,
          secondaryRepMaximum: 1,
          utcDate: 1,
          date: 1,
          sets: 1,
          secondary: 1,
          complex: 1,
          complexInstance: 1,
          complexName: 1,
          thread: 1,
        },
      },
    });
    return await res.json();
  }
);

register(
  {
    name: "exerciseTrendHistory",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async ({ api }, { start, end, proxyAthlete }) => {
    const queryExercise = {
      limit: false,
      sort: { order: 1, date: 1 },
      fields: [
        "_id",
        "primary",
        "secondary",
        "type",
        "sets",
        "utcDate",
        "date",
        "order",
        "complex",
        "complexName",
        "parameters",
      ],
      utcDate: {
        $gte: Number.parseInt(moment(start).format("YYYYMMDD"), 10),
        $lte: Number.parseInt(moment(end).format("YYYYMMDD"), 10),
      },
      $or: [{ cycle: { $exists: false } }, { "cycle.isScheduled": true }],
      proxyAthlete,
    };

    const res = await api("exercise", { query: queryExercise });
    return await res.json();
  }
);

register(
  {
    name: "videoRecord",
    meta: ["no-bust", "video-record"],
    refresh: true,
    ttl: "2 hours",
  },
  async ({ api, locals }, date, athlete, type) => {
    let userId = athlete;
    if (!userId) {
      userId = locals.activeUser._id;
    }

    if (isAfterToday(date)) return {};

    // A broken stored Loom link (e.g. private/deleted video) must not take the
    // whole page down: degrade to "no video" instead of surfacing the error.
    try {
      const res = await api("video-record", {
        query: {
          userId,
          date,
          type,
        },
      });

      const data = await res.json();

      return data?.video?.[0];
    } catch (error) {
      console.error("Failed to load video record", error);
      return null;
    }
  }
);

register(
  {
    name: "exerciseCatalog",
    meta: ["exercise-catalog"],
    ttl: "2 seconds",
  },
  async ({ api }, { primary }) => {
    const query = {
      limit: false,
      sort: { movement: 1 },
    };

    if (primary) query.primary = primary;

    const res = await api("exercise-catalog", {
      query,
    });

    return await res.json();
  }
);

// Public (no-auth) preset list + live counts for the onboarding "Pick a
// starting exercise library" step. Small payload (labels + counts only).
register(
  {
    name: "exerciseLibraryPresets",
    meta: ["exercise-catalog"],
    ttl: "5 minutes",
  },
  async ({ api }) => {
    const res = await api("exercise-library-presets");
    return await res.json();
  }
);

register(
  {
    name: "videoListMetrics",
    meta: ["no-bust", "exercise", "thread"],
    ttl: "2 hours",
  },
  async (
    { api },
    { sort, limit, from, to, proxyAthlete, page, exerciseFilters }
  ) => {
    const res = await api("exercise-video-list", {
      method: "POST",
      body: {
        from,
        sort,
        limit,
        to,
        proxyAthlete,
        page,
        // sent as the JSON string we already keep in the url: the fetcher flattens
        // arrays into form fields and would cast a numeric exercise name to a Number
        exerciseFilters,
      },
    });
    return await res.json();
  }
);

register(
  {
    name: "videoListExerciseNames",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async ({ api }, { from, to, proxyAthlete }) => {
    const res = await api("exercise-video-list-filters", {
      method: "POST",
      body: {
        from,
        to,
        proxyAthlete,
      },
    });
    return await res.json();
  }
);

register(
  {
    name: "weeksExercises",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async ({ api }, { from, to, proxyAthlete }) => {
    const startDate = Number.parseInt(
      moment(from).format(DATE_FORMATS.UTC),
      10
    );
    const endDate = Number.parseInt(moment(to).format(DATE_FORMATS.UTC), 10);
    const query = {
      limit: false,
      sort: { utcDate: 1 },
      utcDate: {
        $gte: startDate,
        $lte: endDate,
      },
      $or: [{ cycle: { $exists: false } }, { "cycle.isScheduled": true }],
      withThreads: true,
    };

    if (proxyAthlete) {
      query.proxyAthlete = proxyAthlete;
    }

    const res = await api("exercise", { query });
    const resJson = await res.json();
    return resJson;
  }
);

register(
  {
    name: "coachVideos",
    meta: ["video-record", "thread"],
    ttl: "2 hours",
  },
  async ({ api }, { athlete, coach, limit }) => {
    const query = {
      userId: athlete,
      coachId: coach,
      videoLimit: limit,
    };

    const res = await api("coach-videos", { query });
    const resJson = await res.json();

    return resJson.videos;
  }
);

register(
  {
    name: "editPreviousExercises",
    meta: ["no-bust", "exercise"],
    ttl: "2 hours",
  },
  async (
    { api, session },
    {
      cycle,
      isScheduled,
      proxyAthlete,
      program,
      forProgramEditor = false,
      organizationCoach = undefined,
      fields = undefined,
    }
  ) => {
    const editExercises = getSessionValue(session, "editPreviousExercises");

    if (editExercises?.length) return editExercises;
    const query = {
      limit: false,
      "cycle.id": cycle,
      "cycle.isScheduled": isScheduled,
      proxyAthlete,
      sort: { order: 1 },
      forProgramEditor,
      organizationCoach,
    };

    if (program) {
      query["cycle.program"] = program;
    }
    if (fields) query.fields = fields;

    const res = await api("exercise", {
      query,
    });

    const result = await res.json();

    return result;
  }
);
